/**
 * HUD compositor (03 §8, §10 HUD; 09 §7): panels on one device-resolution Canvas2D above the GL
 * canvas.
 *
 * - Panels draw in integer stage px (the `HudDraw` primitives map to device px), ordered by
 *   `z` then insertion. Each panel paints inside its paint rect: its `rect`, or with
 *   `clip: false` the union of `rect` and `bounds` (for overflow such as callouts). The paint rect
 *   is what gets cleared, damaged and intersected, so overflow never goes stale. `clip: false`
 *   without `bounds` behaves like `clip: true`.
 * - `removePanel` also drops the hit regions owned by the panel id (owner = panel id) and ends a
 *   hover on them with `onLeave`.
 * - Dirty tracking: a panel redraws when invalidated, when its rect changes, on its cadence
 *   (`'frame'` every flush, `'onChange'` when `key()` changes, a number = Hz buckets of `now()`),
 *   or when a panel overlapping it redraws (only the overlap is redrawn).
 * - `flush()` clears the damaged rects, then redraws every panel that intersects them, clipped to
 *   the intersection. Untouched pixels stay.
 * - Static backgrounds (`PanelSpec.background`) are cached per panel in a device-resolution
 *   canvas and blitted 1:1; transparent pixels in it stay transparent ("holes" over the map).
 *   `invalidateBackground(id)` rebuilds one; `resize` rebuilds all.
 * - Hit regions in stage px with click and hover enter/leave; the shell feeds pointer events in
 *   stage px through `pointer()`.
 */
import type { Cadence, HitRegion, HudApi, PanelSpec, Rect, Size } from '../api.ts';
import { type Ctx2D, DrawImpl, toDevice } from './draw.ts';
import type { PixelFonts } from './font.ts';
import { type CanvasFactory, type HudCanvas, TextCache, type TextCacheOptions, ctx2d, defaultCanvasFactory } from './text-cache.ts';

export interface HudScale {
  /** device px per stage px (f) */
  k: number;
  stage: Size;
}

export interface HudOptions {
  fonts: PixelFonts;
  /** presentation time in seconds for numeric (Hz) cadences; default `performance.now() / 1000` */
  now?: () => number;
  createCanvas?: CanvasFactory;
  textCache?: TextCacheOptions;
  /** size the backbuffer of the main canvas on `resize` (default true) */
  sizeCanvas?: boolean;
}

export type PointerType = 'move' | 'down' | 'up' | 'click' | 'leave';

export interface PointerResult {
  /** owner and id of the region under the pointer, or null */
  owner: string | null;
  id: string | null;
  /** CSS cursor to show, or null for the default */
  cursor: string | null;
  /** true when a click handler ran */
  clicked: boolean;
}

interface PanelRec {
  spec: PanelSpec;
  rect: Rect;
  /** overflow bounds (clip: false), absolute stage px */
  bounds: Rect | null;
  /** paint rect = rect, or rect ∪ bounds when clip: false */
  paint: Rect;
  z: number;
  seq: number;
  dirty: boolean;
  lastKey: string | number | undefined;
  bucket: number;
  bg: HudCanvas | null;
  bgValid: boolean;
}

interface DevRect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function isInt(r: Rect): boolean {
  return Number.isInteger(r.x) && Number.isInteger(r.y) && Number.isInteger(r.w) && Number.isInteger(r.h);
}

function rectEq(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/** The rect a panel may paint into (and that is cleared/damaged for it). */
function paintRect(spec: PanelSpec, rect: Rect, bounds: Rect | null): Rect {
  if (spec.clip !== false || !bounds) return rect;
  const x0 = Math.min(rect.x, bounds.x);
  const y0 = Math.min(rect.y, bounds.y);
  return { x: x0, y: y0, w: Math.max(rect.x + rect.w, bounds.x + bounds.w) - x0, h: Math.max(rect.y + rect.h, bounds.y + bounds.h) - y0 };
}

export class Hud implements HudApi {
  readonly canvas: HudCanvas;
  readonly fonts: PixelFonts;
  readonly text: TextCache;
  private readonly ctx: Ctx2D;
  private readonly draw: DrawImpl;
  private readonly bgDraw: DrawImpl;
  private readonly createCanvas: CanvasFactory;
  private readonly now: () => number;
  private readonly sizeCanvas: boolean;
  private panels = new Map<string, PanelRec>();
  private order: PanelRec[] = [];
  private seq = 0;
  private hits = new Map<string, HitRegion[]>();
  private hover: { owner: string; region: HitRegion } | null = null;
  private readonly ptr: PointerResult = { owner: null, id: null, cursor: null, clicked: false };
  // damage: pending stage rects (from setRect / removePanel) + dirty panel rects, as device rects
  private damage: DevRect[] = [];
  private nDamage = 0;
  private clipBuf: DevRect[] = [];
  private readonly panelDev: DevRect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  private _k = 1;
  private _stage: Size = { w: 1920, h: 1080 };
  private _device: Size = { w: 1920, h: 1080 };
  /** counters for tests and the debug overlay (last flush) */
  readonly stats = { drawn: 0, damaged: 0, flushes: 0, errors: 0, lastError: '' };
  /** panels whose draw threw (logged once each); a failing panel never stops the render loop */
  private readonly failed = new Set<string>();

  private panelError(id: string, e: unknown): void {
    this.stats.errors++;
    this.stats.lastError = `${id}: ${String(e)}`;
    if (!this.failed.has(id)) {
      this.failed.add(id);
      console.error(`hud: panel ${id} failed to draw`, e);
    }
  }

  constructor(canvas: HudCanvas, opts: HudOptions) {
    this.canvas = canvas;
    this.fonts = opts.fonts;
    this.createCanvas = opts.createCanvas ?? defaultCanvasFactory;
    this.now = opts.now ?? (() => performance.now() / 1000);
    this.sizeCanvas = opts.sizeCanvas ?? true;
    this.text = new TextCache({ createCanvas: this.createCanvas, ...opts.textCache });
    this.ctx = ctx2d(canvas);
    this.draw = new DrawImpl(this.ctx, this.fonts, this.text);
    this.bgDraw = new DrawImpl(this.ctx, this.fonts, this.text);
  }

  get k(): number {
    return this._k;
  }
  get stage(): Size {
    return this._stage;
  }
  /** backbuffer size in device px */
  get device(): Size {
    return this._device;
  }

  /** New UI scale or stage size: reallocate the backbuffer and every cache, redraw everything. */
  resize(s: HudScale): void {
    this._k = s.k;
    this._stage = { w: s.stage.w, h: s.stage.h };
    this._device = { w: Math.round(s.stage.w * s.k), h: Math.round(s.stage.h * s.k) };
    if (this.sizeCanvas) {
      this.canvas.width = this._device.w;
      this.canvas.height = this._device.h;
    }
    this.text.setScale(s.k);
    for (const p of this.order) {
      p.bgValid = false;
      p.bg = null;
      p.dirty = true;
    }
    this.nDamage = 0;
    this.addDamage({ x: 0, y: 0, w: s.stage.w, h: s.stage.h });
  }

  // ───────────────────────────── panels ─────────────────────────────

  addPanel(p: PanelSpec): void {
    if (!isInt(p.rect)) throw new Error(`hud: panel ${p.id} rect must be integer stage px`);
    if (p.bounds && !isInt(p.bounds)) throw new Error(`hud: panel ${p.id} bounds must be integer stage px`);
    const old = this.panels.get(p.id);
    if (old) this.detach(p.id); // replacing a panel keeps its hit regions
    const rect = { ...p.rect };
    const bounds = p.bounds ? { ...p.bounds } : null;
    const rec: PanelRec = {
      spec: p,
      rect,
      bounds,
      paint: paintRect(p, rect, bounds),
      z: p.z ?? 0,
      seq: this.seq++,
      dirty: true,
      lastKey: undefined,
      bucket: Number.NaN,
      bg: null,
      bgValid: false,
    };
    this.panels.set(p.id, rec);
    this.order.push(rec);
    this.sortPanels();
  }

  /** Remove a panel and the hit regions it owns (owner = panel id); a hovered one gets onLeave. */
  removePanel(id: string): void {
    if (this.detach(id)) this.setHits(id, []);
  }

  private detach(id: string): boolean {
    const p = this.panels.get(id);
    if (!p) return false;
    this.panels.delete(id);
    this.order.splice(this.order.indexOf(p), 1);
    this.addDamage(p.paint);
    p.bg = null;
    return true;
  }

  hasPanel(id: string): boolean {
    return this.panels.has(id);
  }

  panelRect(id: string): Rect | undefined {
    return this.panels.get(id)?.rect;
  }

  /** New rect (and, for clip: false panels, optionally new overflow bounds). */
  setRect(id: string, r: Rect, bounds?: Rect): void {
    const p = this.panels.get(id);
    if (!p) return;
    if (!isInt(r) || (bounds && !isInt(bounds))) throw new Error(`hud: panel ${id} rect must be integer stage px`);
    if (rectEq(p.rect, r) && (!bounds || (p.bounds && rectEq(p.bounds, bounds)))) return;
    this.addDamage(p.paint);
    if (p.rect.w !== r.w || p.rect.h !== r.h) p.bgValid = false;
    else if (!Number.isInteger(this._k)) p.bgValid = false; // device rounding depends on the position
    p.rect = { x: r.x, y: r.y, w: r.w, h: r.h };
    if (bounds) p.bounds = { x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h };
    p.paint = paintRect(p.spec, p.rect, p.bounds);
    p.dirty = true;
  }

  setZ(id: string, z: number): void {
    const p = this.panels.get(id);
    if (!p || p.z === z) return;
    p.z = z;
    p.dirty = true;
    this.sortPanels();
  }

  invalidate(id?: string): void {
    if (id === undefined) {
      for (const p of this.order) p.dirty = true;
      return;
    }
    const p = this.panels.get(id);
    if (p) p.dirty = true;
  }

  /** Rebuild a panel's cached static background (e.g. after a theme or data change). */
  invalidateBackground(id: string): void {
    const p = this.panels.get(id);
    if (!p) return;
    p.bgValid = false;
    p.dirty = true;
  }

  clear(): void {
    for (const p of this.order) this.addDamage(p.paint);
    this.panels.clear();
    this.order = [];
    if (this.hover) this.hover.region.onLeave?.();
    this.hover = null;
    this.hits.clear();
  }

  private sortPanels(): void {
    this.order.sort((a, b) => a.z - b.z || a.seq - b.seq);
  }

  private devRect(r: Rect, out: DevRect): DevRect {
    const k = this._k;
    out.x0 = Math.max(0, toDevice(r.x, k));
    out.y0 = Math.max(0, toDevice(r.y, k));
    out.x1 = Math.min(this._device.w, toDevice(r.x + r.w, k));
    out.y1 = Math.min(this._device.h, toDevice(r.y + r.h, k));
    return out;
  }

  private addDamage(r: Rect): void {
    if (r.w <= 0 || r.h <= 0) return;
    let d = this.damage[this.nDamage];
    if (!d) {
      d = { x0: 0, y0: 0, x1: 0, y1: 0 };
      this.damage.push(d);
    }
    this.devRect(r, d);
    if (d.x1 > d.x0 && d.y1 > d.y0) this.nDamage++;
  }

  private cadenceDirty(p: PanelRec, t: number): boolean {
    const c: Cadence = p.spec.cadence;
    if (c === 'frame') return true;
    if (c === 'onChange') {
      if (!p.spec.key) return false;
      const k = p.spec.key();
      if (k !== p.lastKey) {
        p.lastKey = k;
        return true;
      }
      return false;
    }
    const b = Math.floor(t * c);
    if (b !== p.bucket) {
      p.bucket = b;
      return true;
    }
    return false;
  }

  /** Redraw dirty panels and everything overlapping them. */
  flush(): void {
    const t = this.now();
    for (const p of this.order) {
      // evaluate the cadence every flush so keys/buckets stay current even when already dirty
      const c = this.cadenceDirty(p, t);
      if (p.dirty || c) {
        p.dirty = false;
        this.addDamage(p.paint);
      }
    }
    this.stats.flushes++;
    this.stats.damaged = this.nDamage;
    this.stats.drawn = 0;
    if (this.nDamage === 0) return;
    const ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    for (let i = 0; i < this.nDamage; i++) {
      const d = this.damage[i];
      if (d) ctx.clearRect(d.x0, d.y0, d.x1 - d.x0, d.y1 - d.y0);
    }
    const pr = this.panelDev;
    for (const p of this.order) {
      this.devRect(p.paint, pr);
      let n = 0;
      for (let i = 0; i < this.nDamage; i++) {
        const d = this.damage[i];
        if (!d) continue;
        const x0 = Math.max(d.x0, pr.x0);
        const y0 = Math.max(d.y0, pr.y0);
        const x1 = Math.min(d.x1, pr.x1);
        const y1 = Math.min(d.y1, pr.y1);
        if (x1 <= x0 || y1 <= y0) continue;
        let c = this.clipBuf[n];
        if (!c) {
          c = { x0: 0, y0: 0, x1: 0, y1: 0 };
          this.clipBuf.push(c);
        }
        c.x0 = x0;
        c.y0 = y0;
        c.x1 = x1;
        c.y1 = y1;
        n++;
      }
      if (n === 0) continue;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const c = this.clipBuf[i];
        if (c) ctx.rect(c.x0, c.y0, c.x1 - c.x0, c.y1 - c.y0);
      }
      ctx.clip();
      if (p.spec.background) {
        const bg = this.background(p);
        if (bg) {
          ctx.imageSmoothingEnabled = false;
          ctx.drawImage(bg, toDevice(p.rect.x, this._k), toDevice(p.rect.y, this._k));
        }
      }
      this.draw.bind(ctx, this._k, 0, 0);
      try {
        p.spec.draw(this.draw, p.rect);
      } catch (e) {
        this.panelError(p.spec.id, e);
      }
      ctx.restore();
      this.stats.drawn++;
    }
    this.nDamage = 0;
  }

  private background(p: PanelRec): HudCanvas | null {
    if (p.bgValid && p.bg) return p.bg;
    if (!p.spec.background) return null;
    const k = this._k;
    const x0 = toDevice(p.rect.x, k);
    const y0 = toDevice(p.rect.y, k);
    const w = toDevice(p.rect.x + p.rect.w, k) - x0;
    const h = toDevice(p.rect.y + p.rect.h, k) - y0;
    if (w <= 0 || h <= 0) return null;
    let c = p.bg;
    if (c?.width !== w || c.height !== h) c = this.createCanvas(w, h);
    const bctx = ctx2d(c);
    bctx.setTransform(1, 0, 0, 1, 0, 0);
    bctx.clearRect(0, 0, w, h);
    this.bgDraw.bind(bctx, k, x0, y0);
    bctx.save();
    try {
      p.spec.background(this.bgDraw, p.rect);
    } catch (e) {
      this.panelError(p.spec.id, e);
    }
    bctx.restore();
    p.bg = c;
    p.bgValid = true;
    return c;
  }

  // ───────────────────────────── hit regions ─────────────────────────────

  setHits(owner: string, regions: HitRegion[]): void {
    if (this.hover?.owner === owner) {
      const id = this.hover.region.id;
      const next = regions.find((r) => r.id === id);
      if (next) this.hover.region = next;
      else {
        this.hover.region.onLeave?.();
        this.hover = null;
      }
    }
    if (regions.length === 0) this.hits.delete(owner);
    else this.hits.set(owner, regions);
  }

  /** Topmost region at a stage point (later owners and later regions win). Allocation-free. */
  hitAt(x: number, y: number): { owner: string; region: HitRegion } | null {
    let found: HitRegion | null = null;
    let foundOwner = '';
    for (const [owner, regions] of this.hits) {
      for (let i = regions.length - 1; i >= 0; i--) {
        const r = regions[i];
        if (!r) continue;
        const q = r.rect;
        if (x >= q.x && x < q.x + q.w && y >= q.y && y < q.y + q.h) {
          found = r;
          foundOwner = owner;
          break;
        }
      }
    }
    if (!found) return null;
    this.hitScratch.owner = foundOwner;
    this.hitScratch.region = found;
    return this.hitScratch;
  }
  private readonly hitScratch: { owner: string; region: HitRegion } = { owner: '', region: { id: '', rect: { x: 0, y: 0, w: 0, h: 0 } } };

  /**
   * Pointer input in stage px (the shell maps CSS px → device px → stage px, 09 §7).
   * 'move'/'down'/'up' update hover (enter/leave), 'click' also runs `onClick`, 'leave' ends hover.
   * Returns a reused result object.
   */
  pointer(x: number, y: number, type: PointerType): PointerResult {
    const res = this.ptr;
    res.clicked = false;
    const h = type === 'leave' ? null : this.hitAt(x, y);
    const prev = this.hover;
    if (prev && (!h || h.region !== prev.region)) {
      prev.region.onLeave?.();
      this.hover = null;
    }
    if (h && !this.hover) {
      this.hover = { owner: h.owner, region: h.region };
      h.region.onEnter?.();
    }
    if (h && type === 'click' && h.region.onClick) {
      h.region.onClick();
      res.clicked = true;
    }
    res.owner = h ? h.owner : null;
    res.id = h ? h.region.id : null;
    res.cursor = h ? (h.region.cursor ?? (h.region.onClick ? 'pointer' : null)) : null;
    return res;
  }
}
